import React from 'react';
import ReactDOM from 'react-dom';
import { useSpring, animated } from 'react-spring';
import { useReduceMotion } from 'react-reduce-motion';

import './styles.css';

const interpolate = (value, start, end, limit) =>
  start + (value / limit) * (end - start);
const calc = (
  rotation,
  scale,
  mouseX,
  mouseY,
  { offsetTop, offsetLeft, offsetWidth, offsetHeight }
) => [
  interpolate(
    mouseY - offsetTop + window.pageYOffset,
    rotation,
    -rotation,
    offsetWidth
  ),
  interpolate(
    mouseX - offsetLeft + window.pageXOffset,
    -rotation,
    rotation,
    offsetHeight
  ),
  scale
];
export const transform = (x, y, s) =>
  `perspective(800px) rotateX(${x}deg) rotateY(${y}deg) scale(${s})`;

function ParallaxAnimatedButton({
  defaultRotation = 10,
  defaultScale = 1.2,
  reducedRotation = defaultRotation / 3,
  reducedScale = 1.02
}) {
  const buttonRef = React.useRef();
  const reduceMotion = useReduceMotion();
  const defaultTransform = [0, 0, 1];
  // This is where we choose the animation intensity depending on user preference.
  const actualRotation = reduceMotion ? reducedRotation : defaultRotation;
  const actualScale = reduceMotion ? reducedScale : defaultScale;
  const [props, set] = useSpring(() => ({
    xys: defaultTransform,
    config: { mass: 7, tension: 500, friction: 40 }
  }));
  return (
    <animated.button
      ref={buttonRef}
      className="springy-button"
      onMouseMove={({ clientX, clientY }) =>
        set({
          xys: calc(
            actualRotation,
            actualScale,
            clientX,
            clientY,
            buttonRef.current
          )
        })
      }
      onMouseLeave={() => set({ xys: defaultTransform })}
      style={{
        transform: props.xys.to(transform)
      }}
    >
      Hover over me!
    </animated.button>
  );
}

function RangeKnob({ name, value, onChange, inputProps }) {
  const knobtions = inputProps[name];
  return (
    <label>
      {name}: {value}
      <input
        type="range"
        min={knobtions.min}
        max={knobtions.max}
        step={knobtions.step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function useKnobs(initialValues, options) {
  const [values, setValues] = React.useState(initialValues);
  return [
    values,
    <div
      style={{
        marginTop: 20,
        width: 200,
        padding: 20,
        backgroundColor: 'lightgrey'
      }}
    >
      {Object.keys(values).map(name => (
        <RangeKnob
          {...options}
          key={name}
          name={name}
          value={values[name]}
          onChange={newValue =>
            setValues({
              ...values,
              [name]: newValue
            })
          }
        />
      ))}
    </div>
  ];
}

const App = () => {
  const [reducedProps, knobs] = useKnobs(
    {
      reducedRotation: 3,
      reducedScale: 1.02
    },
    {
      inputProps: {
        reducedRotation: { min: 0, max: 5, step: 1 },
        reducedScale: { min: 1, max: 1.1, step: 0.01 }
      }
    }
  );
  return (
    <div className="App">
      <h1>React Reduce Motion Example</h1>
      <h2>Toggle "Reduce motion"</h2>
      <p>The animation should be disabled when "Reduce motion" is on</p>
      <img
        alt="reduce motion setting is macos"
        width="500px"
        src="https://lukeherrington.com/static/56a2a145993311eb80344c1b9845f23f/884f2/reduce-motion-macos.png"
      />
      <div>
        <ParallaxAnimatedButton {...reducedProps} />
      </div>
      <p style={{ marginTop: 60 }}>
        These knobs are for demonstation purposes only. They're not part of
        React Reduce Motion.
      </p>
      <div>{knobs}</div>
      <footer style={{ marginTop: 60 }}>
        <h3>Resources</h3>
        <ul>
          <li>
            <a href="https://github.com/infiniteluke/react-reduce-motion">
              repo
            </a>
          </li>
          <li>
            <a href="https://github.com/infiniteluke/react-reduce-motion">
              npm
            </a>
          </li>
          <li>
            <a href="https://twitter.com/lukeherrington">twitter</a>
          </li>
        </ul>
      </footer>
    </div>
  );
};

const rootElement = document.getElementById('root');
ReactDOM.render(<App />, rootElement);
